import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, Loader2, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import ProductCard from '../components/ProductCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Products = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch products and categories from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsRes, categoriesRes, subcategoriesRes] = await Promise.all([
          fetch(`${API_URL}/api/products`),
          fetch(`${API_URL}/api/categories`),
          fetch(`${API_URL}/api/subcategories`)
        ]);
        
        if (!productsRes.ok || !categoriesRes.ok || !subcategoriesRes.ok) {
          throw new Error('Failed to fetch data');
        }
        
        const productsData = await productsRes.json();
        const categoriesData = await categoriesRes.json();
        const subcategoriesData = await subcategoriesRes.json();
        
        setProducts(productsData);
        setFilteredProducts(productsData);
        setCategories(categoriesData);
        setSubcategories(subcategoriesData);
        setError(null);
      } catch (err) {
        setError('Failed to load products. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams]);

  useEffect(() => {
    let filtered = products;

    if (selectedCategory !== 'all') {
      if (selectedCategory === 'dj-equipment') {
        filtered = filtered.filter(p => p.category === 'DJ Equipment');
      } else if (selectedCategory === 'pequod-acoustics') {
        filtered = filtered.filter(p => p.category === 'Pequod Acoustics');
      }
    }

    if (selectedSubcategory !== 'all') {
      filtered = filtered.filter(p => p.subcategory === selectedSubcategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  }, [selectedCategory, selectedSubcategory, searchQuery, products]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedSubcategory('all');
    if (category !== 'all') {
      setSearchParams({ category });
    } else {
      setSearchParams({});
    }
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedSubcategory('all');
    setSearchQuery('');
    setSearchParams({});
  };

  const hasActiveFilters = selectedCategory !== 'all' || selectedSubcategory !== 'all' || searchQuery !== '';

  return (
    <div className="min-h-screen bg-slate-900 pt-20">
      {/* Header */}
      <section className="bg-slate-900 py-16 px-4 sm:px-6 lg:px-8 border-b border-slate-800">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-3 bg-[#e2e8f0] text-slate-800 border-0 px-4 py-1.5 text-xs font-semibold">
            PREMIUM EQUIPMENT HIRE
          </Badge>
          <h1 className="text-4xl font-bold text-white mb-3 heading-font">Our Equipment Range</h1>
          <p className="text-base text-gray-400 max-w-2xl mx-auto">
            Professional DJ equipment and Pequod Acoustics PA systems for 20-2,000 guest events across London and surrounding areas
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar Filters */}
          <aside className="lg:col-span-1">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-5 border border-slate-700/50 sticky top-24 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-bold text-white flex items-center gap-2 heading-font">
                  <Filter className="w-4 h-4 text-gray-400" />
                  Filters
                </h2>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="text-gray-400 hover:text-white hover:bg-slate-700/50 text-xs"
                  >
                    Clear
                  </Button>
                )}
              </div>

              {/* Categories */}
              <div className="mb-5">
                <h3 className="text-xs font-bold text-gray-400 mb-2 uppercase tracking-wide">Categories</h3>
                <div className="space-y-1">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.id)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                        selectedCategory === cat.id
                          ? 'bg-gradient-to-r from-[#e2e8f0] to-[#d1d5db] text-slate-900 shadow-md'
                          : 'text-gray-400 hover:bg-slate-700/50 hover:text-white'
                      }`}
                    >
                      <span className="flex items-center justify-between">
                        <span>{cat.name}</span>
                        <span className={`text-xs ${
                          selectedCategory === cat.id ? 'text-gray-300' : 'text-gray-400'
                        }`}>{cat.count}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3">
            {/* Search Bar */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 border border-slate-700/50 mb-5 shadow-sm">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search equipment by name or description..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10 h-10 border-slate-600 bg-slate-700/50 text-white placeholder:text-gray-400 focus:border-[#e2e8f0] focus:ring-[#e2e8f0] text-sm rounded-lg"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Delivery Note */}
            <div className="bg-slate-800/30 rounded-lg px-4 py-2.5 mb-5 border border-slate-700/30">
              <p className="text-xs text-gray-400">Delivery and collection from £100 (London and surrounding areas). DJ equipment prices exclude VAT at 20%.</p>
            </div>

            {/* Active Filters */}
            {hasActiveFilters && (
              <div className="flex flex-wrap gap-2 mb-5">
                {selectedCategory !== 'all' && (
                  <Badge className="bg-[#e2e8f0] text-slate-800 hover:bg-[#d1d5db] border-0 px-3 py-1 text-xs">
                    {categories.find(c => c.id === selectedCategory)?.name}
                    <button
                      onClick={() => handleCategoryChange('all')}
                      className="ml-2 hover:text-slate-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {selectedSubcategory !== 'all' && (
                  <Badge className="bg-[#e2e8f0] text-slate-800 hover:bg-[#d1d5db] border-0 px-3 py-1 text-xs">
                    {selectedSubcategory}
                    <button
                      onClick={() => setSelectedSubcategory('all')}
                      className="ml-2 hover:text-slate-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
              </div>
            )}

            {/* Results Count */}
            <div className="mb-5">
              <p className="text-sm text-gray-400">
                Showing <span className="font-medium text-white">{filteredProducts.length}</span> equipment item{filteredProducts.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-[#e2e8f0] animate-spin" />
              </div>
            ) : error ? (
              <div className="bg-slate-800/50 rounded-xl p-10 text-center border border-slate-700/50">
                <div className="w-14 h-14 bg-red-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 heading-font">Error Loading Products</h3>
                <p className="text-gray-400 mb-4 text-sm">{error}</p>
                <Button onClick={() => window.location.reload()} className="bg-[#e2e8f0] hover:bg-[#d1d5db] text-slate-900">
                  Try Again
                </Button>
              </div>
            ) : filteredProducts.length > 0 ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="bg-slate-800/50 rounded-xl p-10 text-center border border-slate-700/50">
                <div className="w-14 h-14 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-6 h-6 text-gray-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 heading-font">No equipment found</h3>
                <p className="text-gray-400 mb-4 text-sm">Try adjusting your filters or search query</p>
                <Button onClick={clearFilters} className="bg-[#e2e8f0] hover:bg-[#d1d5db] text-slate-900">
                  Clear All Filters
                </Button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Products;
